import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Paper,
  Group,
  Stack,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Box,
  Divider,
  Loader,
  Center,
  Alert,
  ThemeIcon,
  Badge,
  ActionIcon,
  ScrollArea,
  Slider,
  TagsInput,
  Progress,
  Tooltip,
  Modal
} from '@mantine/core';
import { personasAPI } from '../services/api';
import axios from 'axios';

const CreatePersonaPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPersonaId, setEditingPersonaId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    age: '',
    notes: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    relationship: '',
    favorite_holiday: '',
    gender: '',
    traits: [],
    hobbies: [],
    occupations: [],
    activities: [],
    styles: []
  });

  const [preferences, setPreferences] = useState([]);
  const [completeness, setCompleteness] = useState(0);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [prefForm, setPrefForm] = useState({
    category: 'Technology',
    tag: '',
    weight: 0
  });

  const categories = [
    'Technology', 'Food & Drink', 'Music', 'Sports & Fitness',
    'Books & Reading', 'Movies & TV', 'Gaming', 'Fashion & Style',
    'Travel & Adventure', 'Home & Decor', 'Pets & Animals', 'Art & Crafts',
    'Cooking & Culinary', 'Photography', 'Outdoors & Nature', 'Wellness & Health'
  ];

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editId = searchParams.get('edit');

    if (editId) {
      setIsEditing(true);
      setEditingPersonaId(editId);
      loadPersonaData(editId);
    } else {
      setInitialLoading(false);
    }
  }, [location]);

  const loadPersonaData = async (personaId) => {
    try {
      setInitialLoading(true);
      const response = await personasAPI.getById(personaId);
      const persona = response.data;

      setFormData({
        name: persona.name || '',
        age: persona.age || '',
        notes: persona.notes || '',
        email: persona.email || '',
        phone: persona.phone || '',
        city: persona.city || '',
        state: persona.state || '',
        relationship: persona.relationship || '',
        favorite_holiday: persona.favorite_holiday || '',
        gender: persona.gender || '',
        traits: Array.isArray(persona.traits) ? persona.traits : [],
        hobbies: Array.isArray(persona.hobbies) ? persona.hobbies : [],
        occupations: Array.isArray(persona.occupations) ? persona.occupations : [],
        activities: Array.isArray(persona.activities) ? persona.activities : [],
        styles: Array.isArray(persona.styles) ? persona.styles : []
      });

      // Load completeness
      try {
        const completenessResponse = await axios.get(`/personas/${personaId}/completeness`);
        setCompleteness(completenessResponse.data.completeness_percentage || 0);
      } catch (error) {
        console.error('Failed to load completeness:', error);
      }

      // Load existing preferences
      try {
        const preferencesAPI = await import('../services/api').then(m => m.preferencesAPI);
        const prefsResponse = await preferencesAPI.getForSubject('persona', personaId);
        const existingPrefs = prefsResponse.data.map(pref => ({
          id: pref.id,
          category: pref.category,
          tag: pref.tag,
          weight: pref.weight,
          isExisting: true
        }));
        setPreferences(existingPrefs);
      } catch (error) {
        console.error('Failed to load persona preferences:', error);
      }
    } catch (error) {
      console.error('Failed to load persona data:', error);
      alert(`Failed to load persona data for editing: ${error.response?.data?.error || error.message}`);
      navigate('/personas');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleAddPreference = () => {
    if (!prefForm.tag.trim()) {
      alert('Please enter an interest or preference');
      return;
    }

    setPreferences([...preferences, { ...prefForm, id: Date.now() }]);
    setPrefForm({ category: 'Technology', tag: '', weight: 0 });
  };

  const handleRemovePreference = async (id) => {
    const pref = preferences.find(p => p.id === id);
    
    // If editing and this is an existing preference, delete from server
    if (isEditing && pref?.isExisting) {
      try {
        const preferencesAPI = await import('../services/api').then(m => m.preferencesAPI);
        await preferencesAPI.delete(id);
      } catch (error) {
        console.error('Failed to delete preference:', error);
        alert('Failed to delete preference. Please try again.');
        return;
      }
    }
    
    // Remove from local state
    setPreferences(preferences.filter(p => p.id !== id));
  };

  const getWeightColor = (weight) => {
    if (weight >= 3) return 'teal';
    if (weight >= 1) return 'green';
    if (weight <= -3) return 'red';
    if (weight <= -1) return 'orange';
    return 'gray';
  };

  const getWeightText = (weight) => {
    if (weight > 0) return `+${weight}`;
    return weight === 0 ? '±0' : weight.toString();
  };

  const handleGetAISuggestions = async () => {
    if (!editingPersonaId) return;
    
    setAiLoading(true);
    try {
      const response = await axios.post(`/personas/${editingPersonaId}/suggest-attributes`, {});
      
      setAiSuggestions(response.data);
      setShowAIModal(true);
    } catch (error) {
      console.error('AI suggestion error:', error);
      alert(error.response?.data?.message || 'Failed to get AI suggestions. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplySuggestions = (suggestions) => {
    if (!suggestions) return;
    
    setFormData({
      ...formData,
      traits: [...new Set([...formData.traits, ...(suggestions.traits || [])])],
      hobbies: [...new Set([...formData.hobbies, ...(suggestions.hobbies || [])])],
      occupations: [...new Set([...formData.occupations, ...(suggestions.occupations || [])])],
      activities: [...new Set([...formData.activities, ...(suggestions.activities || [])])],
      styles: [...new Set([...formData.styles, ...(suggestions.styles || [])])]
    });
    
    setShowAIModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert('Persona name is required');
      return;
    }

    setLoading(true);
    try {
      let personaId;
      if (isEditing) {
        await personasAPI.update(editingPersonaId, formData);
        personaId = editingPersonaId;
        
        // Handle preference changes when editing
        const preferencesAPI = await import('../services/api').then(m => m.preferencesAPI);
        
        // Get original preference IDs
        const originalPrefIds = new Set(
          preferences.filter(p => p.isExisting).map(p => p.id)
        );
        
        // Create new preferences (ones without isExisting flag)
        for (const pref of preferences.filter(p => !p.isExisting)) {
          try {
            await preferencesAPI.create({
              subject_type: 'persona',
              subject_id: personaId,
              category: pref.category,
              tag: pref.tag,
              weight: pref.weight
            });
          } catch (error) {
            console.error('Failed to create preference:', error);
          }
        }
        
        // Note: Deleted preferences are already removed via handleRemovePreference
        // which can be enhanced to delete from server immediately
      } else {
        const response = await personasAPI.create(formData);
        personaId = response.data.id;
        
        // Create all preferences for new persona
        if (preferences.length > 0) {
          const preferencesAPI = await import('../services/api').then(m => m.preferencesAPI);
          for (const pref of preferences) {
            try {
              await preferencesAPI.create({
                subject_type: 'persona',
                subject_id: personaId,
                category: pref.category,
                tag: pref.tag,
                weight: pref.weight
              });
            } catch (error) {
              console.error('Failed to create preference:', error);
            }
          }
        }
      }

      navigate('/personas');
    } catch (error) {
      alert(`Failed to ${isEditing ? 'update' : 'create'} persona: ` + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Center h="70vh">
        <Stack align="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text c="dimmed" size="lg">Loading persona data...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Box style={{ background: 'linear-gradient(180deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)' }}>
      <Container size="md" py="xl" px="md">
        {/* Header */}
        <Box mb="xl" py="lg">
          <Group justify="space-between" align="flex-start" mb="lg">
            <Box>
              <Title 
                order={1} 
                size="h1"
                fw={700}
                style={{
                  background: 'linear-gradient(45deg, #1971c2 0%, #15aabf 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px'
                }}
              >
                🎭 {isEditing ? 'Edit Persona' : 'Create New Persona'}
              </Title>
              <Text size="xl" c="dimmed" fw={400}>
                {isEditing ? 'Update the persona details' : 'Add a recipient profile to find the perfect gifts'}
              </Text>
            </Box>
            <Button 
              variant="outline"
              size="md"
              onClick={() => navigate('/personas')}
            >
              ← Back
            </Button>
          </Group>
        </Box>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Stack gap="xl">
            {/* Main Info Card */}
            <Paper
              p="xl"
              radius="lg"
              withBorder
              shadow="sm"
              style={{
                background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
              }}
            >
              <Stack gap="lg">
                <Box>
                  <Group gap="xs" mb="lg">
                    <ThemeIcon size="lg" radius="md" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
                      👤
                    </ThemeIcon>
                    <Text size="lg" fw={600}>Basic Information</Text>
                  </Group>

                  <Stack gap="md">
                    <TextInput
                      label="Persona Name"
                      placeholder="e.g., Tech-Savvy Sarah"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                      size="md"
                      description="Give your persona a memorable, descriptive name"
                    />

                    <NumberInput
                      label="Age (optional)"
                      placeholder="e.g., 25"
                      value={formData.age}
                      onChange={(value) => handleInputChange('age', value)}
                      size="md"
                      min={0}
                      max={120}
                      description="Helps with age-appropriate gift recommendations"
                    />
                  </Stack>
                </Box>

                <Divider label="📞 Contact Information" labelPosition="center" />

                <Group grow>
                  <TextInput
                    label="Email Address"
                    placeholder="email@example.com"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    size="md"
                    description="For gift delivery options"
                  />
                  <TextInput
                    label="Phone Number"
                    placeholder="(555) 123-4567"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    size="md"
                    description="For holiday wishes"
                  />
                </Group>

                <Divider label="📍 Location" labelPosition="center" />

                <Group grow>
                  <TextInput
                    label="City"
                    placeholder="San Francisco"
                    value={formData.city}
                    onChange={(e) => handleInputChange('city', e.target.value)}
                    size="md"
                    description="Helps suggest local experiences"
                  />
                  <TextInput
                    label="State"
                    placeholder="California"
                    value={formData.state}
                    onChange={(e) => handleInputChange('state', e.target.value)}
                    size="md"
                    description="For seasonal gift suggestions"
                  />
                </Group>

                <Divider label="💝 Relationship & Celebrations" labelPosition="center" />

                <Group grow>
                  <Select
                    label="Relationship Type"
                    placeholder="Choose relationship..."
                    data={[
                      { value: 'family', label: 'Family Member' },
                      { value: 'friend', label: 'Close Friend' },
                      { value: 'colleague', label: 'Work Colleague' },
                      { value: 'partner', label: 'Romantic Partner' },
                      { value: 'acquaintance', label: 'Acquaintance' }
                    ]}
                    value={formData.relationship}
                    onChange={(value) => handleInputChange('relationship', value)}
                    size="md"
                    description="Helps customize gift suggestions"
                    clearable
                  />
                  <Select
                    label="Favorite Holiday"
                    placeholder="Choose holiday..."
                    data={[
                      { value: 'birthday', label: 'Birthday' },
                      { value: 'christmas', label: 'Christmas' },
                      { value: 'hanukkah', label: 'Hanukkah' },
                      { value: 'thanksgiving', label: 'Thanksgiving' },
                      { value: 'easter', label: 'Easter' },
                      { value: 'mothers-day', label: "Mother's Day" },
                      { value: 'fathers-day', label: "Father's Day" },
                      { value: 'valentines', label: "Valentine's Day" },
                      { value: 'halloween', label: 'Halloween' },
                      { value: 'anniversary', label: 'Anniversary' },
                      { value: 'graduation', label: 'Graduation' }
                    ]}
                    value={formData.favorite_holiday}
                    onChange={(value) => handleInputChange('favorite_holiday', value)}
                    size="md"
                    description="Suggests holiday-specific gifts"
                    clearable
                  />
                </Group>

                <Divider label="✍️ Personality & Preferences" labelPosition="center" />

                <Textarea
                  label="Personality Notes"
                  placeholder="Describe this person's personality, hobbies, interests, style preferences, dietary preferences, favorite colors, brands they love, etc."
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={5}
                  size="md"
                  description="More details help find better, more personalized gift matches"
                />
              </Stack>
            </Paper>

            {/* Enhanced Persona Attributes Card - NEW */}
            <Paper
              p="xl"
              radius="lg"
              withBorder
              shadow="sm"
              style={{
                background: 'linear-gradient(135deg, rgba(139,92,246,0.03) 0%, rgba(0,0,0,0) 100%)'
              }}
            >
              <Stack gap="lg">
                <Box>
                  <Group justify="space-between" mb="md">
                    <Group gap="xs">
                      <ThemeIcon size="lg" radius="md" variant="gradient" gradient={{ from: 'violet', to: 'indigo' }}>
                        ✨
                      </ThemeIcon>
                      <Box>
                        <Text size="lg" fw={600}>Enhanced Persona Profile</Text>
                        <Text size="sm" c="dimmed">Add detailed attributes for better gift recommendations</Text>
                      </Box>
                    </Group>
                    {isEditing && completeness > 0 && (
                      <Tooltip label={`Profile ${completeness}% complete`}>
                        <Badge 
                          size="xl" 
                          variant="gradient" 
                          gradient={{ from: completeness >= 80 ? 'teal' : completeness >= 50 ? 'blue' : 'orange', to: 'cyan' }}
                        >
                          {completeness}%
                        </Badge>
                      </Tooltip>
                    )}
                  </Group>

                  {isEditing && (
                    <Box mb="md">
                      <Progress 
                        value={completeness} 
                        size="lg" 
                        radius="xl"
                        color={completeness >= 80 ? 'teal' : completeness >= 50 ? 'blue' : 'orange'}
                      />
                      <Text size="xs" c="dimmed" mt="xs" ta="center">
                        Profile Completeness
                      </Text>
                    </Box>
                  )}
                </Box>

                <Divider label="👤 Identity" labelPosition="center" />

                <Select
                  label="Gender (Optional)"
                  placeholder="Select gender..."
                  data={[
                    { value: 'male', label: 'Male' },
                    { value: 'female', label: 'Female' },
                    { value: 'non-binary', label: 'Non-binary' },
                    { value: 'other', label: 'Other' },
                    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
                  ]}
                  value={formData.gender}
                  onChange={(value) => handleInputChange('gender', value)}
                  size="md"
                  description="Helps personalize gift suggestions"
                  clearable
                />

                <Divider label="🎨 Personality Traits" labelPosition="center" />

                <TagsInput
                  label="Personality Traits"
                  placeholder="Type and press Enter (e.g., adventurous, creative, introverted)"
                  value={formData.traits}
                  onChange={(value) => handleInputChange('traits', value)}
                  size="md"
                  description="Add traits that describe their personality"
                  maxTags={10}
                />

                <Divider label="💼 Occupations" labelPosition="center" />

                <TagsInput
                  label="Occupations / Professions"
                  placeholder="Type and press Enter (e.g., software engineer, teacher)"
                  value={formData.occupations}
                  onChange={(value) => handleInputChange('occupations', value)}
                  size="md"
                  description="Their current or past professions"
                  maxTags={5}
                />

                <Divider label="🎯 Hobbies & Interests" labelPosition="center" />

                <TagsInput
                  label="Hobbies"
                  placeholder="Type and press Enter (e.g., photography, hiking, cooking)"
                  value={formData.hobbies}
                  onChange={(value) => handleInputChange('hobbies', value)}
                  size="md"
                  description="What they love to do in their free time"
                  maxTags={10}
                />

                <Divider label="🏃 Activities" labelPosition="center" />

                <TagsInput
                  label="Regular Activities"
                  placeholder="Type and press Enter (e.g., yoga, traveling, volunteering)"
                  value={formData.activities}
                  onChange={(value) => handleInputChange('activities', value)}
                  size="md"
                  description="Things they do regularly"
                  maxTags={10}
                />

                <Divider label="👔 Style Preferences" labelPosition="center" />

                <TagsInput
                  label="Style & Aesthetic"
                  placeholder="Type and press Enter (e.g., modern, minimalist, vintage)"
                  value={formData.styles}
                  onChange={(value) => handleInputChange('styles', value)}
                  size="md"
                  description="Their preferred aesthetic and style"
                  maxTags={8}
                />

                {/* AI Suggestion Button */}
                {isEditing && (
                  <Alert color="violet" variant="light" icon={<span>🤖</span>}>
                    <Group justify="space-between">
                      <Box>
                        <Text size="sm" fw={500}>Need help filling these out?</Text>
                        <Text size="xs" c="dimmed">AI can analyze this persona and suggest attributes</Text>
                      </Box>
                      <Button 
                        variant="gradient"
                        gradient={{ from: 'violet', to: 'purple' }}
                        size="sm"
                        loading={aiLoading}
                        onClick={handleGetAISuggestions}
                        leftSection={<span>✨</span>}
                      >
                        Get AI Suggestions
                      </Button>
                    </Group>
                  </Alert>
                )}
              </Stack>
            </Paper>

            {/* Preferences Card */}
            <Paper
              p="xl"
              radius="lg"
              withBorder
              shadow="sm"
              style={{
                background: 'linear-gradient(135deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)'
              }}
            >
              <Stack gap="lg">
                <Box>
                  <Group gap="xs" mb="md">
                    <ThemeIcon size="lg" radius="md" variant="gradient" gradient={{ from: 'grape', to: 'pink' }}>
                      🎯
                    </ThemeIcon>
                    <Box>
                      <Text size="lg" fw={600}>
                        {isEditing ? 'Manage Preferences' : 'Initial Preferences (Optional)'}
                      </Text>
                      <Text size="sm" c="dimmed">
                        {isEditing 
                          ? 'Add, modify, or remove preferences to refine AI recommendations'
                          : 'Add 3-5 key interests to seed AI recommendations'}
                      </Text>
                    </Box>
                  </Group>
                </Box>

                  {/* Add Preference Form */}
                  <Paper p="md" withBorder radius="md">
                    <Stack gap="sm">
                      <Select
                        label="Category"
                        data={categories}
                        value={prefForm.category}
                        onChange={(value) => setPrefForm({ ...prefForm, category: value })}
                        size="sm"
                      />
                      <TextInput
                        label="Interest/Tag"
                        placeholder="e.g., mechanical keyboards, specialty coffee"
                        value={prefForm.tag}
                        onChange={(e) => setPrefForm({ ...prefForm, tag: e.target.value })}
                        size="sm"
                      />
                      <Box>
                        <Group justify="space-between" mb="xs">
                          <Text size="sm" fw={500}>Preference Strength</Text>
                          <Badge 
                            size="lg" 
                            color={getWeightColor(prefForm.weight)}
                            variant="filled"
                          >
                            {getWeightText(prefForm.weight)}
                          </Badge>
                        </Group>
                        <Slider
                          value={prefForm.weight}
                          onChange={(value) => setPrefForm({ ...prefForm, weight: value })}
                          min={-5}
                          max={5}
                          step={1}
                          marks={[
                            { value: -5, label: '😡' },
                            { value: -3, label: '😕' },
                            { value: 0, label: '😐' },
                            { value: 3, label: '😊' },
                            { value: 5, label: '🤩' }
                          ]}
                          color={
                            prefForm.weight >= 3 ? 'teal' :
                            prefForm.weight >= 1 ? 'green' :
                            prefForm.weight <= -3 ? 'red' :
                            prefForm.weight <= -1 ? 'orange' : 'gray'
                          }
                          styles={{
                            markLabel: { fontSize: '16px' }
                          }}
                        />
                        <Group justify="space-between" mt="xs">
                          <Text size="xs" c="dimmed">Avoid/Dislike</Text>
                          <Text size="xs" c="dimmed">Love/Essential</Text>
                        </Group>
                      </Box>
                      <Button size="sm" onClick={handleAddPreference} fullWidth>
                        Add Preference
                      </Button>
                    </Stack>
                  </Paper>

                  {/* Preferences List */}
                  {preferences.length > 0 && (
                    <Box>
                      <Text size="sm" fw={600} mb="xs">
                        Added Preferences ({preferences.length})
                      </Text>
                      <ScrollArea h={200}>
                        <Stack gap="xs">
                          {preferences.map((pref) => (
                            <Paper key={pref.id} p="xs" withBorder radius="md">
                              <Group justify="space-between">
                                <Group gap="xs">
                                  <Badge size="xs" variant="light">
                                    {pref.category}
                                  </Badge>
                                  <Text size="sm">{pref.tag}</Text>
                                </Group>
                                <Group gap="xs">
                                  <Badge
                                    size="sm"
                                    color={getWeightColor(pref.weight)}
                                    variant="filled"
                                  >
                                    {getWeightText(pref.weight)}
                                  </Badge>
                                  <ActionIcon
                                    size="sm"
                                    color="red"
                                    variant="subtle"
                                    onClick={() => handleRemovePreference(pref.id)}
                                  >
                                    ✕
                                  </ActionIcon>
                                </Group>
                              </Group>
                            </Paper>
                          ))}
                        </Stack>
                      </ScrollArea>
                      <Text size="xs" c="dimmed" mt="xs">
                        {isEditing 
                          ? 'Changes will be saved when you update the persona'
                          : 'You can add more preferences after creating the persona'}
                      </Text>
                    </Box>
                  )}
                </Stack>
              </Paper>

            {/* Tips Card */}
            <Alert
              icon={
                <ThemeIcon size="xl" radius="xl" variant="light" color="blue">
                  💡
                </ThemeIcon>
              }
              title="Tips for Creating Great Personas"
              color="blue"
              variant="light"
              radius="lg"
            >
              <Stack gap="xs">
                <Text size="sm">
                  <strong>• Use descriptive names:</strong> "Adventure-Seeking Alex" or "Bookish Becca"
                </Text>
                <Text size="sm">
                  <strong>• Include interests:</strong> Hobbies, favorite colors, brands they love
                </Text>
                <Text size="sm">
                  <strong>• Add personality:</strong> How they spend weekends, their favorite activities
                </Text>
                <Text size="sm">
                  <strong>• Note preferences:</strong> Dietary needs, clothing styles, tech preferences
                </Text>
                <Text size="sm">
                  <strong>• Be specific:</strong> More details = better gift recommendations
                </Text>
              </Stack>
            </Alert>

            {/* Action Buttons */}
            <Group grow>
              <Button
                type="submit"
                size="xl"
                variant="gradient"
                gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
                loading={loading}
                style={{ boxShadow: '0 4px 12px rgba(25,113,194,0.3)' }}
              >
                {isEditing ? '✏️ Update Persona' : '✨ Create Persona'}
              </Button>
              <Button
                type="button"
                size="xl"
                variant="outline"
                onClick={() => navigate('/personas')}
                disabled={loading}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        </form>

        {/* AI Suggestions Modal */}
        <Modal
          opened={showAIModal}
          onClose={() => setShowAIModal(false)}
          title={
            <Group gap="xs">
              <ThemeIcon size="lg" variant="gradient" gradient={{ from: 'violet', to: 'purple' }}>
                🤖
              </ThemeIcon>
              <Box>
                <Text size="lg" fw={600}>AI Suggestions</Text>
                <Text size="xs" c="dimmed">
                  Based on {aiSuggestions?.persona_name || 'this persona'}
                  {aiSuggestions?.confidence && ` • Confidence: ${aiSuggestions.confidence}`}
                </Text>
              </Box>
            </Group>
          }
          size="lg"
          centered
        >
          {aiSuggestions?.suggestions && (
            <Stack gap="md">
              {aiSuggestions.suggestions.traits?.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} mb="xs">🎨 Personality Traits</Text>
                  <Group gap="xs">
                    {aiSuggestions.suggestions.traits.map((trait, idx) => (
                      <Badge key={idx} variant="light" size="lg">{trait}</Badge>
                    ))}
                  </Group>
                </Box>
              )}

              {aiSuggestions.suggestions.hobbies?.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} mb="xs">🎯 Hobbies</Text>
                  <Group gap="xs">
                    {aiSuggestions.suggestions.hobbies.map((hobby, idx) => (
                      <Badge key={idx} variant="light" size="lg" color="teal">{hobby}</Badge>
                    ))}
                  </Group>
                </Box>
              )}

              {aiSuggestions.suggestions.activities?.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} mb="xs">🏃 Activities</Text>
                  <Group gap="xs">
                    {aiSuggestions.suggestions.activities.map((activity, idx) => (
                      <Badge key={idx} variant="light" size="lg" color="blue">{activity}</Badge>
                    ))}
                  </Group>
                </Box>
              )}

              {aiSuggestions.suggestions.occupations?.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} mb="xs">💼 Occupations</Text>
                  <Group gap="xs">
                    {aiSuggestions.suggestions.occupations.map((occupation, idx) => (
                      <Badge key={idx} variant="light" size="lg" color="grape">{occupation}</Badge>
                    ))}
                  </Group>
                </Box>
              )}

              {aiSuggestions.suggestions.styles?.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} mb="xs">👔 Styles</Text>
                  <Group gap="xs">
                    {aiSuggestions.suggestions.styles.map((style, idx) => (
                      <Badge key={idx} variant="light" size="lg" color="pink">{style}</Badge>
                    ))}
                  </Group>
                </Box>
              )}

              <Group grow mt="md">
                <Button
                  variant="gradient"
                  gradient={{ from: 'violet', to: 'purple' }}
                  onClick={() => handleApplySuggestions(aiSuggestions.suggestions)}
                >
                  Apply All Suggestions
                </Button>
                <Button variant="outline" onClick={() => setShowAIModal(false)}>
                  Cancel
                </Button>
              </Group>

              {aiSuggestions.error && (
                <Alert color="orange" variant="light" mt="md">
                  <Text size="sm">{aiSuggestions.error}</Text>
                </Alert>
              )}
            </Stack>
          )}
        </Modal>
      </Container>
    </Box>
  );
};

export default CreatePersonaPage;
